/**
 * Room Type Display Priority Sequence
 * 
 * This defines the order in which room types should be displayed
 * across the application (Availability Calendar, Reports, etc.)
 */

// Room Type Priority Order (lower number = higher priority in display)
export const ROOM_TYPE_PRIORITY = {
  // Men's Dormitory
  'men dormitory': 1,
  'mens dormitory': 1,
  'male dormitory': 1,
  
  // Women's Dormitory
  'women dormitory': 2,
  'womens dormitory': 2,
  'female dormitory': 2,
  
  // Daya Rooms
  'daya private room': 3,
  'daya female shared room': 4,
  'daya male shared room': 5,
  
  // Meera Rooms
  'meera private room': 6,
  'meera female shared room': 7,
  'meera male shared room': 8,
  
  // Radha Room
  'radha room': 9,
  
  // Special Rooms
  'penthouse': 10,
  'attic room': 11,
  'living area': 12,
  'master bedroom': 13
};

// Alternative names mapping (for backward compatibility and variations)
export const ROOM_TYPE_ALIASES = {
  'daya dorm': 'daya male shared room',
  'meera dorm': 'meera male shared room',
  'bhuri dorm': 'men dormitory',
  'male dorm': 'male dormitory',
  'female dorm': 'female dormitory',
  'daya room': 'daya private room',
  'meera room': 'meera private room'
};

/**
 * Normalize room type name for comparison
 * @param {string} roomTypeName - Raw room type name
 * @returns {string} - Normalized lowercase room type name
 */
export const normalizeRoomTypeName = (roomTypeName) => {
  if (!roomTypeName) return '';
  
  let normalized = String(roomTypeName).toLowerCase().trim();
  
  // Check if there's an alias mapping
  if (ROOM_TYPE_ALIASES[normalized]) {
    normalized = ROOM_TYPE_ALIASES[normalized];
  }
  
  return normalized;
};

/**
 * Get priority value for a room type
 * @param {string} roomTypeName - Room type name
 * @returns {number} - Priority value (lower = higher priority)
 */
export const getRoomTypePriority = (roomTypeName) => {
  const normalized = normalizeRoomTypeName(roomTypeName);
  return ROOM_TYPE_PRIORITY[normalized] || 999; // Unknown types go to end
};

/**
 * Extract room type name from various data structures
 * @param {object} item - Booking, room, or data object
 * @returns {string} - Extracted room type name
 */
export const extractRoomTypeName = (item) => {
  if (!item) return '';
  
  // Direct properties
  if (item.roomTypeName) {
    return item.roomTypeName;
  }
  
  if (item.roomType) {
    // Could be string or object
    if (typeof item.roomType === 'string') {
      return item.roomType;
    }
    if (item.roomType.name) {
      return item.roomType.name;
    }
  }
  
  // Check in nested room object
  if (item.room) {
    if (item.room.roomType) {
      if (typeof item.room.roomType === 'string') {
        return item.room.roomType;
      }
      if (item.room.roomType.name) {
        return item.room.roomType.name;
      }
    }
  }
  
  // Check in rooms array
  if (item.rooms && Array.isArray(item.rooms) && item.rooms.length > 0) {
    const firstRoom = item.rooms[0];
    if (firstRoom.roomTypeName) {
      return firstRoom.roomTypeName;
    }
    if (firstRoom.room?.roomType?.name) {
      return firstRoom.room.roomType.name;
    }
  }
  
  // Check in bookedRooms array
  if (item.bookedRooms && Array.isArray(item.bookedRooms) && item.bookedRooms.length > 0) {
    const firstRoom = item.bookedRooms[0];
    if (firstRoom.roomTypeName) {
      return firstRoom.roomTypeName;
    }
    if (firstRoom.room?.roomType?.name) {
      return firstRoom.room.roomType.name;
    }
  }
  
  // Check flattened columns (rooms_1_roomTypeName, rooms_roomTypeName)
  const roomTypeKeys = Object.keys(item).filter(key => 
    key.match(/^rooms_\d+_roomTypeName$/) || key === 'rooms_roomTypeName'
  );
  
  if (roomTypeKeys.length > 0) {
    const value = item[roomTypeKeys[0]];
    if (value) {
      // Handle comma-separated values (take first)
      const firstValue = String(value).split(',')[0].trim();
      return firstValue;
    }
  }
  
  return '';
};

/**
 * Sort rooms by room type priority
 * @param {Array} rooms - Array of room objects
 * @returns {Array} - Sorted array
 */
export const sortRoomsByType = (rooms) => {
  if (!rooms || !Array.isArray(rooms) || rooms.length === 0) {
    return rooms;
  }
  
  return [...rooms].sort((a, b) => {
    const roomTypeA = extractRoomTypeName(a);
    const roomTypeB = extractRoomTypeName(b);
    
    const priorityA = getRoomTypePriority(roomTypeA);
    const priorityB = getRoomTypePriority(roomTypeB);
    
    // Sort by priority (ascending = higher priority first)
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // If same priority, sort by room number (if available)
    const roomNumberA = a.roomNumber || a.number || 0;
    const roomNumberB = b.roomNumber || b.number || 0;
    
    return roomNumberA - roomNumberB;
  });
};

/**
 * Sort bookings by room type priority
 * @param {Array} bookings - Array of booking objects
 * @returns {Array} - Sorted array
 */
export const sortBookingsByRoomType = (bookings) => {
  if (!bookings || !Array.isArray(bookings) || bookings.length === 0) {
    return bookings;
  }
  
  return [...bookings].sort((a, b) => {
    const roomTypeA = extractRoomTypeName(a);
    const roomTypeB = extractRoomTypeName(b);
    
    const priorityA = getRoomTypePriority(roomTypeA);
    const priorityB = getRoomTypePriority(roomTypeB);
    
    // Sort by priority (ascending = higher priority first)
    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }
    
    // If same priority, maintain original order
    return 0;
  });
};

/**
 * Group rooms by room type
 * @param {Array} rooms - Array of room objects
 * @returns {Object} - Object with room type names as keys and arrays of rooms as values
 */
export const groupRoomsByType = (rooms) => {
  if (!rooms || !Array.isArray(rooms)) {
    return {};
  }
  
  const grouped = {};
  
  rooms.forEach(room => {
    const roomTypeName = extractRoomTypeName(room);
    const normalized = normalizeRoomTypeName(roomTypeName);
    
    if (!grouped[normalized]) {
      grouped[normalized] = [];
    }
    
    grouped[normalized].push(room);
  });
  
  return grouped;
};

/**
 * Get sorted list of room type names
 * @param {Array} rooms - Array of room objects
 * @returns {Array} - Sorted array of unique room type names
 */
export const getSortedRoomTypeNames = (rooms) => {
  if (!rooms || !Array.isArray(rooms)) {
    return [];
  }
  
  const uniqueTypes = new Set();
  
  rooms.forEach(room => {
    const roomTypeName = extractRoomTypeName(room);
    if (roomTypeName) {
      uniqueTypes.add(roomTypeName);
    }
  });
  
  const typeArray = Array.from(uniqueTypes);
  
  return typeArray.sort((a, b) => {
    const priorityA = getRoomTypePriority(a);
    const priorityB = getRoomTypePriority(b);
    return priorityA - priorityB;
  });
};

export default {
  ROOM_TYPE_PRIORITY,
  ROOM_TYPE_ALIASES,
  normalizeRoomTypeName,
  getRoomTypePriority,
  extractRoomTypeName,
  sortRoomsByType,
  sortBookingsByRoomType,
  groupRoomsByType,
  getSortedRoomTypeNames
};
